import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, RefreshCw, LogIn } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface AdminErrorStateProps {
  /**
   * Error message to display
   */
  error: string | null;

  /**
   * Component title (e.g., "System Health", "Audit Logs")
   */
  title?: string;

  /**
   * Optional callback for retry action
   */
  onRetry?: () => void;

  /**
   * Whether to show the "Go to Login" button
   * @default true
   */
  showLoginButton?: boolean;

  /**
   * Whether to show the "Retry" button
   * @default true
   */
  showRetryButton?: boolean;

  /**
   * Custom icon component
   */
  icon?: React.ReactNode;
}

/**
 * AdminErrorState - Consistent error UI for admin components
 *
 * Provides a standardized error display with:
 * - Clear error messaging
 * - Retry functionality
 * - Redirect to login
 * - Consistent styling
 *
 * @example
 * ```tsx
 * <AdminErrorState
 *   title="System Health"
 *   error="Authentication required. Please log in."
 *   onRetry={() => fetchHealthData()}
 * />
 * ```
 */
export function AdminErrorState({
  error,
  title = 'Admin Component',
  onRetry,
  showLoginButton = true,
  showRetryButton = true,
  icon,
}: AdminErrorStateProps) {
  const navigate = useNavigate();

  const handleGoToLogin = () => {
    navigate('/login');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {icon || <AlertCircle className="h-5 w-5 text-destructive" />}
          {title}
        </CardTitle>
        <CardDescription className="text-destructive font-medium">
          {error || 'An error occurred while loading data.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col sm:flex-row gap-3">
          {showRetryButton && onRetry && (
            <Button onClick={onRetry} variant="default" className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Retry
            </Button>
          )}
          {showLoginButton && (
            <Button onClick={handleGoToLogin} variant="outline" className="gap-2">
              <LogIn className="h-4 w-4" />
              Go to Login
            </Button>
          )}
        </div>

        {/* Helpful hint for developers/admins */}
        {error?.includes('Authentication') && (
          <div className="mt-4 p-3 bg-muted rounded-md">
            <p className="text-sm text-muted-foreground">
              <strong>Note:</strong> You need to be logged in as a super admin to access this feature.
              If you believe this is an error, please try logging out and back in again.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
